import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog } from '@/shared/components/Dialog/Dialog';
import { TextInput } from '@/shared/components/TextInput/TextInput';
import { PillButton } from '@/shared/components/PillButton/PillButton';
import { NavButton } from '@/shared/components/NavButton/NavButton';
import { createTextStroke } from '@/styles/textStroke';

export interface FirstLoginDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmit: (data: { username: string; country: string }) => void;
  countries: { code: string; name: string }[];
  defaultCountry?: string;
  error?: string | null;
  onErrorClear?: () => void;
  className?: string;
}

const messageStyle = createTextStroke('1.5px');

export function FirstLoginDialog({
  isOpen,
  onClose,
  onSubmit,
  countries,
  defaultCountry = '',
  error,
  onErrorClear,
  className = ''
}: FirstLoginDialogProps) {
  const [username, setUsername] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const selectedCountryName = countries.find((c) => c.code === selectedCountry)?.name ?? 'PAYS';

  // Update dropdown position when opening and on scroll/resize
  useEffect(() => {
    if (!isDropdownOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isDropdownOpen]);

  // Auto-close dropdown when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Click-outside-to-close behavior
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Keyboard navigation for dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setIsDropdownOpen(false);
  };

  const handleSubmit = () => {
    if (username.trim() && selectedCountry) {
      onSubmit({ username: username.trim(), country: selectedCountry });
    }
  };

  const isFormValid = username.trim().length > 0 && selectedCountry.length > 0;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      variant="blue"
      title="Première connexion"
      showCloseButton={false}
      allowClickOutsideToClose={false}
      className={`max-w-md ${className}`}
    >
      <div className="p-6 space-y-6">
        {/* Welcome Message */}
        <div className="text-center">
          <p className="text-white font-mind-boggle text-xl leading-tight" style={messageStyle}>
            bienvenue chez SOKA
          </p>
          <p
            className="text-white font-mind-boggle text-xl leading-tight mt-4"
            style={messageStyle}
          >
            avant de jouer, veuillez renseigner votre pays et choisir un pseudo.
          </p>
        </div>

        {/* Username Input */}
        <div className="space-y-2">
          <TextInput
            variant="light-grey"
            placeholder="PSEUDO"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (error) onErrorClear?.();
            }}
            maxLength={20}
            className="w-full h-14"
            aria-label="Pseudo"
          />
          {error && (
            <p className="text-red-400 font-bold text-sm text-center" style={messageStyle}>
              {error}
            </p>
          )}
        </div>

        {/* Country Dropdown */}
        <div className="space-y-2" ref={buttonRef}>
          <NavButton
            variant="yellow"
            arrowDirection="down"
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
            className="w-full h-14 [&_span]:!text-base [&_span]:!leading-tight"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            {selectedCountryName}
          </NavButton>
        </div>

        {/* Country Dropdown Menu - Rendered via Portal */}
        {isDropdownOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="bg-[#4A4A4A] border-[3px] border-black rounded-lg overflow-hidden max-h-60 overflow-y-auto"
              style={{
                position: 'fixed',
                top: `${String(dropdownPosition.top)}px`,
                left: `${String(dropdownPosition.left)}px`,
                width: `${String(dropdownPosition.width)}px`,
                zIndex: 9999
              }}
              role="listbox"
              aria-label="Select country"
            >
              {countries.map((country) => (
                <button
                  key={country.code}
                  onClick={() => {
                    handleCountrySelect(country.code);
                  }}
                  className={`w-full px-4 py-3 text-white font-bold text-sm font-mind-boggle text-left hover:bg-[#5A5A5A] transition-colors ${
                    selectedCountry === country.code ? 'bg-[#5A5A5A]' : ''
                  }`}
                  style={createTextStroke('1.5px')}
                  role="option"
                  aria-selected={selectedCountry === country.code}
                >
                  {country.name}
                </button>
              ))}
            </div>,
            document.body
          )}

        {/* Submit Button */}
        <div className="pt-2">
          <PillButton
            variant="green"
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="w-full h-14 [&_span]:!text-xl [&_span]:!leading-tight"
          >
            OK
          </PillButton>
        </div>
      </div>
    </Dialog>
  );
}

export default FirstLoginDialog;
